// Dashboard Ejecutivo — solo accesible al Fiscal de Estado y delegados.
// Organizado en 5 tabs con lazy load: cada tab carga su data al activarse.
// La URL refleja la tab actual con ?tab= para compartir links directos.

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, CircularProgress, Alert,
    IconButton, Tooltip, Tabs, Tab, Stack,
    Menu, MenuItem, ListItemIcon, ListItemText, Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GavelIcon from '@mui/icons-material/Gavel';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArticleIcon from '@mui/icons-material/Article';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseIcon from '@mui/icons-material/Pause';
import { ToggleButton, ToggleButtonGroup, TextField } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { dashboardEjecutivoAccesoJWT, dashEjecAlertasJWT, dashEjecBuscarAbogadosJWT, dashEjecInvalidarCacheJWT, dashEjecInfoCacheJWT } from '../../api/endpoints';
import { parseFechaServer } from '../../utils/fechas';
import { descargarDashboardEjecutivoPdf } from '../../utils/descargarPdf';
import DelegadosDialog from './DelegadosDialog';
import AlertasBanner from './AlertasBanner';
import AlertasConfigDialog from './AlertasConfigDialog';
import AbogadoDetalleDialog from './AbogadoDetalleDialog';
import { Autocomplete } from '@mui/material';
import TabResumen from './TabResumen';
import TabFinanciero from './TabFinanciero';
import TabOperativo from './TabOperativo';
import TabComparativas from './TabComparativas';
import TabPersonal from './TabPersonal';
import TabProductividad from './TabProductividad';
import TabAsignacion from './TabAsignacion';
import TabComunicado from './TabComunicado';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';

const PRIMARY = '#1769aa';
const BR = 4;

// Helpers para los presets de período del filtro de fecha.
const fmtIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const PERIODOS = {
    mes:        () => { const h = new Date(); const d = new Date(h.getFullYear(), h.getMonth(), 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    trimestre:  () => { const h = new Date(); const d = new Date(h.getFullYear(), h.getMonth() - 2, 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    ano:        () => { const h = new Date(); const d = new Date(h.getFullYear(), 0, 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    anoAnt:     () => { const y = new Date().getFullYear() - 1; return { desde: `${y}-01-01`, hasta: `${y}-12-31` }; },
};

// Tabs que respetan el filtro de período (datos varían según el rango).
// Los demás muestran snapshot a la fecha actual (Financiero, Operativo, Personal):
// las queries detrás son acumulados/totales que no tendrían sentido filtrar
// por período histórico (ej. monto demandado total, causas estancadas hoy,
// personal activo, jubilables próximos). Se muestra un cartel chico aclarando.
const TABS_CON_FILTRO = new Set(['resumen', 'productividad', 'comparativas', 'asignacion', 'comunicado']);

// "Última actualización: HH:mm" (hoy) o "ayer HH:mm" (si fue ayer) o fecha completa.
function textoUltimaActualizacion(d) {
    if (!d) return 'Cargando datos...';
    const now = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (now.toDateString() === d.toDateString()) return `Última actualización: ${hh}:${mm}`;
    const ayer = new Date(now); ayer.setDate(ayer.getDate() - 1);
    if (ayer.toDateString() === d.toDateString()) return `Última actualización: ayer ${hh}:${mm}`;
    return `Última actualización: ${d.toLocaleDateString('es-AR')} ${hh}:${mm}`;
}

const TABS = [
    { key: 'resumen',       label: 'Resumen',       icon: <HomeIcon fontSize="small" />,           Component: TabResumen },
    { key: 'financiero',    label: 'Financiero',    icon: <AttachMoneyIcon fontSize="small" />,    Component: TabFinanciero },
    { key: 'comunicado',    label: 'Comunicado',    icon: <CampaignIcon fontSize="small" />,       Component: TabComunicado },
    { key: 'operativo',     label: 'Operativo',     icon: <GavelIcon fontSize="small" />,          Component: TabOperativo },
    { key: 'asignacion',    label: 'Asignación',    icon: <AssignmentIndIcon fontSize="small" />,  Component: TabAsignacion },
    { key: 'comparativas',  label: 'Comparativas',  icon: <CompareArrowsIcon fontSize="small" />,  Component: TabComparativas },
    { key: 'productividad', label: 'Productividad', icon: <TrendingUpIcon fontSize="small" />,     Component: TabProductividad },
    { key: 'personal',      label: 'Personal',      icon: <PeopleIcon fontSize="small" />,         Component: TabPersonal },
];

export default function DashboardEjecutivo() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [acceso, setAcceso] = useState(null);
    const [loadingAcceso, setLoadingAcceso] = useState(true);
    const [delegadosOpen, setDelegadosOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // Filtro de período. Default: mes actual. Aplica solo al tab Resumen por ahora.
    const [periodoPreset, setPeriodoPreset] = useState('mes');
    const [periodoDesde, setPeriodoDesde] = useState(() => PERIODOS.mes().desde);
    const [periodoHasta, setPeriodoHasta] = useState(() => PERIODOS.mes().hasta);
    // Modo del tab Comunicado (histórico completo vs período del filtro). Vive acá
    // —no en el tab— para que el export a PDF respete el mismo alcance que la vista.
    const [comunicadoHistorico, setComunicadoHistorico] = useState(true);

    const setPreset = (preset) => {
        setPeriodoPreset(preset);
        if (preset !== 'custom') {
            const { desde, hasta } = PERIODOS[preset]();
            setPeriodoDesde(desde);
            setPeriodoHasta(hasta);
        }
    };

    // Exportación a PDF
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const [exportando, setExportando] = useState(false);
    const [exportError, setExportError] = useState('');

    // Alertas (banner + config)
    const [alertas, setAlertas] = useState([]);
    const [alertasMeta, setAlertasMeta] = useState({});
    const [alertasConfigOpen, setAlertasConfigOpen] = useState(false);
    const [alertasReloadKey, setAlertasReloadKey] = useState(0);

    // Info de cache: cuándo fue la última actualización de los datos.
    // Refresh cada 60s y después de cada Refrescar/auto-refresh.
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
    const cargarInfoCache = useCallback(async () => {
        try {
            const r = await dashEjecInfoCacheJWT();
            if (!r?.ok) return;
            const d = parseFechaServer(r.lastWarmupAt);
            // Solo pisar el estado si tenemos un valor válido — evita parpadeos
            // a "Cargando datos..." si un poll devuelve null momentáneamente.
            if (d) setUltimaActualizacion(d);
        } catch { /* silent */ }
    }, []);
    useEffect(() => {
        if (!acceso?.acceso) return;
        cargarInfoCache();
        const id = setInterval(cargarInfoCache, 60 * 1000);
        return () => clearInterval(id);
    }, [acceso, reloadKey, cargarInfoCache]);

    // Abogado detalle (modal compartido — se abre desde Operativo, Productividad
    // y desde el buscador del header).
    const [abogadoSeleccionado, setAbogadoSeleccionado] = useState(null);
    const [abogadoSearchOptions, setAbogadoSearchOptions] = useState([]);
    const [abogadoSearchInput, setAbogadoSearchInput] = useState('');
    const [abogadoSearchLoading, setAbogadoSearchLoading] = useState(false);
    useEffect(() => {
        if (!abogadoSearchInput || abogadoSearchInput.trim().length < 2) {
            setAbogadoSearchOptions([]); return;
        }
        let cancel = false;
        setAbogadoSearchLoading(true);
        const t = setTimeout(() => {
            dashEjecBuscarAbogadosJWT(abogadoSearchInput.trim())
                .then(r => { if (!cancel && r?.ok) setAbogadoSearchOptions(r.data || []); })
                .finally(() => { if (!cancel) setAbogadoSearchLoading(false); });
        }, 250);
        return () => { cancel = true; clearTimeout(t); };
    }, [abogadoSearchInput]);

    // Auto-refresh — toggle persistido en localStorage. Cuando está activo,
    // refresca el tab activo + alertas cada AUTO_REFRESH_MS. Como cada tab
    // tiene cache 5min en backend, este intervalo cae con eso.
    const AUTO_REFRESH_MS = 5 * 60 * 1000;
    const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('dashEjec_autoRefresh') === '1');
    useEffect(() => { localStorage.setItem('dashEjec_autoRefresh', autoRefresh ? '1' : '0'); }, [autoRefresh]);
    useEffect(() => {
        if (!autoRefresh || !acceso?.acceso) return;
        const id = setInterval(async () => {
            // Invalidar cache backend para que el reload traiga datos frescos.
            // Sin esto, el TTL de 5h del cache haría que auto-refresh sea inútil.
            try { await dashEjecInvalidarCacheJWT(); } catch { /* ignorar */ }
            setReloadKey(k => k + 1);
            setAlertasReloadKey(k => k + 1);
            cargarInfoCache();
        }, AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, acceso, AUTO_REFRESH_MS, cargarInfoCache]);

    const handleExportar = async (seccion) => {
        setExportMenuAnchor(null);
        setExportando(true);
        setExportError('');
        try {
            // Solo mandamos desde/hasta cuando la sección las usa,
            // así el filename y la portada no muestran un período engañoso.
            let usaPeriodo = seccion === 'todo' || TABS_CON_FILTRO.has(seccion);
            // Excepción: el tab Comunicado en modo "histórico completo" NO debe
            // mandar período (sino el PDF queda filtrado al mes actual).
            if (seccion === 'comunicado' && comunicadoHistorico) usaPeriodo = false;
            await descargarDashboardEjecutivoPdf({
                seccion,
                desde: usaPeriodo ? periodoDesde : undefined,
                hasta: usaPeriodo ? periodoHasta : undefined,
                preset: usaPeriodo ? periodoPreset : undefined,
            });
        } catch (e) {
            setExportError(e.message || 'Error al generar PDF');
        } finally {
            setExportando(false);
        }
    };

    const tabActiva = searchParams.get('tab') || 'resumen';
    const tabIndex = Math.max(0, TABS.findIndex(t => t.key === tabActiva));

    useEffect(() => {
        let cancel = false;
        dashboardEjecutivoAccesoJWT().then(r => {
            if (cancel) return;
            setAcceso(r);
        }).finally(() => { if (!cancel) setLoadingAcceso(false); });
        return () => { cancel = true; };
    }, []);

    // Cargar alertas cuando hay acceso. Refrescar cuando se cambia config
    // o cuando el usuario aprieta "refrescar" del header.
    useEffect(() => {
        if (!acceso?.acceso) return;
        let cancel = false;
        dashEjecAlertasJWT().then(r => {
            if (cancel || !r?.ok) return;
            setAlertas(r.data.alertas || []);
            setAlertasMeta(r.data.meta || {});
        });
        return () => { cancel = true; };
    }, [acceso, alertasReloadKey, reloadKey]);

    const irATab = (tabKey) => {
        setSearchParams({ tab: tabKey }, { replace: true });
    };

    const handleChangeTab = (_, newIndex) => {
        const tab = TABS[newIndex]?.key || 'resumen';
        setSearchParams({ tab }, { replace: true });
    };

    if (loadingAcceso) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}><CircularProgress /></Box>;
    }
    if (!acceso?.acceso) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                    No tenés acceso al Dashboard Ejecutivo. Si necesitás verlo, pedile al Fiscal de Estado que te agregue como delegado.
                </Alert>
            </Box>
        );
    }

    const ActiveTab = TABS[tabIndex].Component;

    return (
        <Box sx={{ p: 2.5, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
            {/* Header */}
            <Paper sx={{ p: 2.5, borderRadius: `${BR}px`, mb: 2,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #2196f3 100%)`, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Dashboard Ejecutivo</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                            Foto del estado de la Fiscalía · {textoUltimaActualizacion(ultimaActualizacion)} · {acceso.esFiscal ? 'Fiscal' : acceso.esDelegado ? 'Delegado' : acceso.modo === 'DIRECTOR' ? 'Director / Subdirector — su área' : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Exportar a PDF">
                            <span>
                                <IconButton
                                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                                    disabled={exportando}
                                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' } }}>
                                    {exportando ? <CircularProgress size={22} sx={{ color: 'white' }} /> : <PictureAsPdfIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
                            <MenuItem onClick={() => handleExportar('todo')}>
                                <ListItemIcon><LibraryBooksIcon fontSize="small" /></ListItemIcon>
                                <ListItemText
                                    primary="Todo el dashboard"
                                    secondary="Las 6 secciones con portada"
                                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                                />
                            </MenuItem>
                            <MenuItem onClick={() => handleExportar(tabActiva)}>
                                <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
                                <ListItemText
                                    primary={`Solo este tab — ${TABS[tabIndex]?.label || ''}`}
                                    secondary="La sección activa"
                                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                                />
                            </MenuItem>
                        </Menu>
                        {(acceso.esFiscal || acceso.esDelegado || acceso.esDirector) && (
                            <Tooltip title={acceso.esDirector ? 'Configurar mis alertas (de mi área)' : 'Configurar alertas'}>
                                <IconButton onClick={() => setAlertasConfigOpen(true)}
                                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                                    <NotificationsActiveIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {acceso.esFiscal && (
                            <Tooltip title="Gestionar delegados">
                                <IconButton onClick={() => setDelegadosOpen(true)}
                                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                                    <GroupsIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={autoRefresh ? 'Auto-refresh activo (cada 5 min) — click para detener' : 'Activar auto-refresh cada 5 min'}>
                            <IconButton onClick={() => setAutoRefresh(v => !v)}
                                sx={{ color: 'white', bgcolor: autoRefresh ? 'rgba(76,175,80,0.55)' : 'rgba(255,255,255,0.15)',
                                    '&:hover': { bgcolor: autoRefresh ? 'rgba(76,175,80,0.7)' : 'rgba(255,255,255,0.25)' } }}>
                                {autoRefresh ? <PauseIcon /> : <AutorenewIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Refrescar (forzar datos nuevos)">
                            <IconButton
                                onClick={async () => {
                                    // Invalidar cache backend antes de remontar — sino el TTL
                                    // de 5h evita que se vean cambios recientes.
                                    try { await dashEjecInvalidarCacheJWT(); } catch { /* ignorar */ }
                                    setReloadKey(k => k + 1);
                                    setAlertasReloadKey(k => k + 1);
                                    cargarInfoCache();   // refresca el "Última actualización"
                                }}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {/* Banner de alertas — solo si hay alguna disparada */}
            <AlertasBanner alertas={alertas} meta={alertasMeta} onIrATab={irATab} />

            {/* Tabs */}
            <Paper sx={{ borderRadius: `${BR}px`, mb: 2 }}>
                <Tabs value={tabIndex} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto"
                    sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
                          '& .MuiTabs-indicator': { bgcolor: PRIMARY } }}>
                    {TABS.map(t => (
                        <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start"
                            sx={{ '&.Mui-selected': { color: PRIMARY } }} />
                    ))}
                </Tabs>
            </Paper>

            {/* Buscador de abogado — siempre visible, abre el detalle */}
            <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                    Buscar abogado:
                </Typography>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 320, flex: 1, maxWidth: 500 }}
                    options={abogadoSearchOptions}
                    loading={abogadoSearchLoading}
                    getOptionLabel={(o) => o?.nombre ? `${o.nombre}${o.matricula ? ` · Mat. ${o.matricula}` : ''}` : ''}
                    filterOptions={(x) => x}
                    value={null}
                    inputValue={abogadoSearchInput}
                    onInputChange={(_, v) => setAbogadoSearchInput(v)}
                    onChange={(_, v) => {
                        if (v?.Id) {
                            setAbogadoSeleccionado(v.Id);
                            setAbogadoSearchInput('');
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Apellido, nombre o matrícula… (mín 2 letras)" />
                    )}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    noOptionsText={abogadoSearchInput.length < 2 ? 'Empezá a tipear…' : 'Sin coincidencias'}
                />
            </Paper>

            {/* Filtro de período — siempre visible. Cuando el tab actual no
                lo usa (Financiero, Operativo, Personal son snapshot a la fecha)
                se muestra deshabilitado con un cartel para que el Fiscal sepa
                que ese tab muestra datos actuales independientemente del filtro. */}
            {(() => {
                const aplica = TABS_CON_FILTRO.has(tabActiva);
                return (
                    <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                        opacity: aplica ? 1 : 0.55, transition: 'opacity 0.15s' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                            Período:
                        </Typography>
                        <ToggleButtonGroup size="small" exclusive value={periodoPreset}
                            onChange={(_, v) => v && setPreset(v)} disabled={!aplica}>
                            <ToggleButton value="mes" sx={{ textTransform: 'none' }}>Mes actual</ToggleButton>
                            <ToggleButton value="trimestre" sx={{ textTransform: 'none' }}>Trimestre</ToggleButton>
                            <ToggleButton value="ano" sx={{ textTransform: 'none' }}>Año actual</ToggleButton>
                            <ToggleButton value="anoAnt" sx={{ textTransform: 'none' }}>Año anterior</ToggleButton>
                            <ToggleButton value="custom" sx={{ textTransform: 'none' }}>Personalizado</ToggleButton>
                        </ToggleButtonGroup>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField type="date" size="small" label="Desde" value={periodoDesde} disabled={!aplica}
                                onChange={(e) => { setPeriodoDesde(e.target.value); setPeriodoPreset('custom'); }}
                                InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                            <TextField type="date" size="small" label="Hasta" value={periodoHasta} disabled={!aplica}
                                onChange={(e) => { setPeriodoHasta(e.target.value); setPeriodoPreset('custom'); }}
                                InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                        </Stack>
                        {!aplica && (
                            <Typography variant="caption" sx={{ ml: 'auto', color: '#888', fontStyle: 'italic' }}>
                                Este tab muestra datos a la fecha actual — el período no aplica
                            </Typography>
                        )}
                    </Paper>
                );
            })()}

            {/* Contenido del tab activo (con key para forzar remount al refrescar) */}
            <Box>
                <ActiveTab key={`${tabActiva}-${reloadKey}-${periodoDesde}-${periodoHasta}`}
                    desde={periodoDesde} hasta={periodoHasta} preset={periodoPreset}
                    acceso={acceso}
                    historico={comunicadoHistorico} onHistoricoChange={setComunicadoHistorico}
                    onClickAbogado={(idPersonal) => setAbogadoSeleccionado(idPersonal)} />
            </Box>

            <DelegadosDialog open={delegadosOpen} onClose={() => setDelegadosOpen(false)} />

            <AlertasConfigDialog
                open={alertasConfigOpen}
                onClose={() => setAlertasConfigOpen(false)}
                onChange={() => setAlertasReloadKey(k => k + 1)}
            />

            <AbogadoDetalleDialog
                open={!!abogadoSeleccionado}
                idPersonal={abogadoSeleccionado}
                onClose={() => setAbogadoSeleccionado(null)}
                initialPreset={periodoPreset}
                initialDesde={periodoDesde}
                initialHasta={periodoHasta}
            />

            <Snackbar
                open={Boolean(exportError)}
                autoHideDuration={6000}
                onClose={() => setExportError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setExportError('')} variant="filled">
                    {exportError}
                </Alert>
            </Snackbar>
        </Box>
    );
}
